import { useEffect, useState } from 'react';
import { useAuth } from '../state/auth';
import { useToast } from '../state/toast';
import ConfirmDialog from '../ui/ConfirmDialog';
import { PlusIcon } from '../ui/icons';
import type { Board, Theme } from '@blackboard/shared';

type Pending =
  | { kind: 'archive'; board: BoardRow }
  | { kind: 'unarchive'; board: BoardRow }
  | { kind: 'setDefault'; board: BoardRow };

interface BoardRow extends Board {
  online: number;
  stroke_count: number;
}

const THEME_LABEL: Record<Theme, string> = {
  classic_blackboard: '经典黑板',
  dark_blackboard: '纯黑板',
  whiteboard: '白板',
};

const THEME_DOT: Record<Theme, string> = {
  classic_blackboard: '#1b4a2c',
  dark_blackboard: '#0a0a0a',
  whiteboard: '#e5e7eb',
};

function inviteUrl(slug: string): string {
  if (typeof window === 'undefined') return `/b/${slug}`;
  return `${window.location.origin}/b/${slug}`;
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    // Fallback for non-secure contexts.
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export function BoardsTable() {
  const token = useAuth((s) => s.token)!;
  const pushToast = useToast((s) => s.push);
  const [boards, setBoards] = useState<BoardRow[]>([]);
  const [creating, setCreating] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const [form, setForm] = useState({
    slug: '',
    name: '',
    theme: 'classic_blackboard' as Theme,
    is_default: false,
  });
  const [err, setErr] = useState('');

  async function reload() {
    const res = await fetch('/api/admin/boards', {
      headers: { authorization: `Bearer ${token}` },
    });
    if (res.ok) setBoards(await res.json());
  }
  useEffect(() => {
    reload();
  }, []);

  async function create() {
    setErr('');
    const res = await fetch('/api/admin/boards', {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify(form),
    });
    if (!res.ok) {
      setErr((await res.json()).error || `http_${res.status}`);
      return;
    }
    setCreating(false);
    const slug = form.slug;
    setForm({ slug: '', name: '', theme: 'classic_blackboard', is_default: false });
    reload();
    // Auto-copy invite link so the admin can paste it into a 群 (R2-P0-1).
    const url = inviteUrl(slug);
    const ok = await copyToClipboard(url);
    pushToast(
      ok ? 'success' : 'info',
      ok ? `画板 ${slug} 已创建，邀请链接已复制：${url}` : `画板 ${slug} 已创建。链接：${url}`,
      6000
    );
  }

  async function copyInvite(slug: string) {
    const url = inviteUrl(slug);
    const ok = await copyToClipboard(url);
    pushToast(ok ? 'success' : 'error', ok ? `已复制 ${url}` : `复制失败，请手动复制：${url}`, 4000);
  }

  async function patch(id: number, body: Partial<BoardRow>) {
    await fetch(`/api/admin/boards/${id}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    reload();
  }

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-[17px] font-semibold tracking-tight">画板</h2>
          <p className="text-xs text-neutral-500 mt-0.5">
            {boards.length} 个画板 ·{' '}
            {boards.reduce((n, b) => n + b.online, 0)} 人在线
          </p>
        </div>
        <button
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium"
          onClick={() => setCreating((s) => !s)}
        >
          <PlusIcon size={14} />
          新建画板
        </button>
      </div>

      {creating && (
        <div className="bb-fade-in mb-4 p-4 bg-white rounded-xl border border-neutral-200 flex gap-2 flex-wrap items-center">
          <Input
            placeholder="slug (小写字母数字 _-)"
            value={form.slug}
            onChange={(v) => setForm({ ...form, slug: v })}
          />
          <Input
            placeholder="名称"
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
          />
          <select
            className="h-9 px-2.5 rounded-lg border border-neutral-200 bg-white text-sm"
            value={form.theme}
            onChange={(e) => setForm({ ...form, theme: e.target.value as Theme })}
          >
            <option value="classic_blackboard">经典黑板</option>
            <option value="dark_blackboard">纯黑板</option>
            <option value="whiteboard">白板</option>
          </select>
          <label className="inline-flex items-center gap-1.5 text-sm text-neutral-700">
            <input
              type="checkbox"
              checked={form.is_default}
              onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
              className="accent-neutral-900"
            />
            设为默认
          </label>
          <button
            className="h-9 px-3 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium"
            onClick={create}
          >
            创建
          </button>
          <button
            className="h-9 px-3 rounded-lg text-sm text-neutral-600 hover:bg-neutral-100"
            onClick={() => setCreating(false)}
          >
            取消
          </button>
          {err && <span className="text-red-600 text-sm ml-auto">{err}</span>}
        </div>
      )}

      <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500 bg-neutral-50 border-b border-neutral-200">
              <th className="px-4 py-2.5 font-medium">名称</th>
              <th className="px-4 py-2.5 font-medium">Slug</th>
              <th className="px-4 py-2.5 font-medium">主题</th>
              <th className="px-4 py-2.5 font-medium text-right">在线</th>
              <th className="px-4 py-2.5 font-medium text-right">笔画</th>
              <th className="px-4 py-2.5 font-medium text-center">状态</th>
              <th className="px-4 py-2.5 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {boards.map((b) => (
              <tr
                key={b.id}
                className="border-b border-neutral-100 last:border-none hover:bg-neutral-50/60"
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{b.name}</span>
                    {b.is_default && (
                      <span className="inline-flex items-center rounded-md bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
                        默认
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-neutral-500">{b.slug}</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1.5 text-xs text-neutral-700">
                    <span
                      className="w-3 h-3 rounded-full border border-neutral-200"
                      style={{ background: THEME_DOT[b.theme] }}
                    />
                    {THEME_LABEL[b.theme]}
                  </span>
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  <span className={b.online > 0 ? 'text-emerald-700 font-semibold' : 'text-neutral-400'}>
                    {b.online}
                  </span>
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-neutral-600">
                  {b.stroke_count.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-center">
                  {b.archived_at ? (
                    <span className="inline-flex items-center rounded-md bg-neutral-100 px-2 py-0.5 text-[11px] font-medium text-neutral-600">
                      已归档
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                      正常
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    {!b.archived_at && (
                      <button
                        className="h-7 px-2.5 rounded-md text-xs text-brand-700 hover:bg-brand-50"
                        onClick={() => copyInvite(b.slug)}
                      >
                        复制链接
                      </button>
                    )}
                    {!b.is_default && !b.archived_at && (
                      <button
                        className="h-7 px-2.5 rounded-md text-xs text-blue-700 hover:bg-blue-50"
                        onClick={() => setPending({ kind: 'setDefault', board: b })}
                      >
                        设默认
                      </button>
                    )}
                    <button
                      className="h-7 px-2.5 rounded-md text-xs text-neutral-600 hover:bg-neutral-100"
                      onClick={() => setPending({ kind: b.archived_at ? 'unarchive' : 'archive', board: b })}
                    >
                      {b.archived_at ? '恢复' : '归档'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {boards.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-sm text-neutral-400">
                  暂无画板
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={pending !== null}
        title={
          pending?.kind === 'archive'
            ? `归档画板 “${pending.board.name}”？`
            : pending?.kind === 'unarchive'
            ? `恢复画板 “${pending.board.name}”？`
            : pending?.kind === 'setDefault'
            ? `把 “${pending.board.name}” 设为默认画板？`
            : ''
        }
        destructive={pending?.kind === 'archive'}
        confirmLabel={
          pending?.kind === 'archive' ? '归档' : pending?.kind === 'unarchive' ? '恢复' : '设为默认'
        }
        body={
          pending?.kind === 'archive' ? (
            <>
              归档后用户无法访问该画板，但数据保留。该操作可在此恢复。
              <br />当前在线 {pending.board.online} 人 · 笔画 {pending.board.stroke_count.toLocaleString()} 条。
            </>
          ) : pending?.kind === 'unarchive' ? (
            <>恢复后用户即可重新访问。</>
          ) : pending?.kind === 'setDefault' ? (
            <>未指定画板时，新登录用户会被引导到这块画板。</>
          ) : null
        }
        onCancel={() => setPending(null)}
        onConfirm={async () => {
          if (!pending) return;
          if (pending.kind === 'archive') await patch(pending.board.id, { archived: true } as any);
          else if (pending.kind === 'unarchive') await patch(pending.board.id, { archived: false } as any);
          else await patch(pending.board.id, { is_default: true });
          setPending(null);
          pushToast('success', '操作完成。', 2500);
        }}
      />
    </section>
  );
}

function Input({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <input
      placeholder={placeholder}
      className="h-9 px-3 rounded-lg border border-neutral-200 bg-white text-sm outline-none focus:border-neutral-900 placeholder:text-neutral-400"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}
